import { useMemo, useState } from "react";
import { ScrollText, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAudit } from "@/api/hooks";

export function AuditPage() {
    const audit = useAudit();
    const [filter, setFilter] = useState("");

    const visible = useMemo(() => {
        const q = filter.trim().toLowerCase();
        const all = audit.data ?? [];
        if (q.length === 0) return all;
        return all.filter((row) =>
            row.actorEmail.toLowerCase().includes(q) ||
            row.action.toLowerCase().includes(q) ||
            (row.subject ?? "").toLowerCase().includes(q)
        );
    }, [filter, audit.data]);

    return (
        <div className="mx-auto max-w-5xl">
            <header className="mb-4 flex items-center gap-2">
                <ScrollText className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-semibold">Audit log</h1>
            </header>

            <div className="relative mb-3 max-w-sm">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <Input
                    type="search"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter by actor, action, or subject"
                    aria-label="Filter audit log"
                    className="h-8 pl-7 text-sm"
                />
            </div>

            {audit.isPending ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
            ) : audit.isError ? (
                <p className="text-sm text-destructive">{audit.error?.message}</p>
            ) : visible.length > 0 ? (
                <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2 text-left">Time</th>
                                <th className="px-3 py-2 text-left">Actor</th>
                                <th className="px-3 py-2 text-left">Action</th>
                                <th className="px-3 py-2 text-left">Subject</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {visible.map((row) => (
                                <tr key={row.id}>
                                    <td className="px-3 py-2 text-xs text-muted-foreground">
                                        {new Date(row.occurredAt).toLocaleString()}
                                    </td>
                                    <td className="px-3 py-2">{row.actorEmail}</td>
                                    <td className="px-3 py-2">
                                        <code className="rounded bg-muted px-1 py-0.5 text-xs">{row.action}</code>
                                    </td>
                                    <td className="px-3 py-2 text-muted-foreground">{row.subject ?? ""}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : audit.data && audit.data.length > 0 ? (
                <p className="text-sm text-muted-foreground">No events match &ldquo;{filter}&rdquo;.</p>
            ) : (
                <p className="text-sm text-muted-foreground">No audit events yet.</p>
            )}
        </div>
    );
}
